import { Component, OnInit, OnDestroy, DestroyRef, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, of } from 'rxjs';
import { CostCenter } from '../../models/cost-center.model';
import { CostCenterService } from '../../services/cost-center.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { ServiceGroup } from '../../models/service-group.model';
import { AuthService } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-cost-center-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    Select,
    InputText,
    Tooltip,
    ConfirmDialog
  ],
  templateUrl: './cost-center-management.component.html',
  styleUrl: './cost-center-management.component.scss',
  providers: [ConfirmationService]
})
export class CostCenterManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private ngZone = inject(NgZone);
  costCenters: CostCenter[] = [];
  filteredCostCenters: CostCenter[] = [];
  selectedCostCenter: CostCenter = this.getEmptyCostCenter();
  displayDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  loading = false;
  searchText: string = '';
  selectedStatusFilter: string | null = 'active';
  selectedServiceGroupFilter: string | null = null;
  serviceGroups: ServiceGroup[] = [];
  fieldTouched: Record<string, boolean> = {};

  rows: number = 50;
  first: number = 0;
  totalRecords: number = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50];

  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  get totalCostCentersCount(): number { return this.costCenters.length; }
  get activeCostCentersCount(): number { return this.costCenters.filter(c => c.isActive).length; }
  get inactiveCostCentersCount(): number { return this.costCenters.filter(c => !c.isActive).length; }

  constructor(
    private costCenterService: CostCenterService,
    private serviceGroupService: ServiceGroupService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by name or description...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Cost Center');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadServiceGroups();
    this.loadCostCenters();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.showAddDialog());
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.ngZone.run(() => this.exportToExcel());
    });
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.pageHeaderService.setCanShowPrimaryAction(!!user?.isAdmin);
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  private loadServiceGroups(): void {
    this.serviceGroupService.getAllServiceGroups().subscribe({
      next: (groups) => {
        this.serviceGroups = (groups || []).filter(sg => sg.isActive === true);
      },
      error: () => {
        this.serviceGroups = [];
      }
    });
  }

  loadCostCenters() {
    this.loading = true;
    this.costCenterService.getAllCostCenters().pipe(
      catchError(error => {
        console.error('Error loading cost centers:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load cost centers. Please refresh the page or try again later.'
        });
        this.loading = false;
        // Return empty array to prevent complete failure
        return of([] as CostCenter[]);
      })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        // Hide the delivery-side mirrors of presales cost centers. One is created for every
        // presales cost center because allocations can only reference the MAIN table, which
        // is why every presales name used to appear on this screen as well as on Presales
        // Cost Centers. They are maintained from that page, so this list leaves them out.
        //
        // Only mirrors that nothing but presales work uses are hidden — once a delivery
        // project references one it is a real cost center and stays manageable here.
        //
        // The filter is deliberately HERE and not in the API: the same endpoint feeds the
        // allocation dropdowns, which must keep receiving these rows.
        this.costCenters = data
          .filter(cc => !cc.isPresalesMirrorOnly)
          .map(cc => ({
            ...cc,
            isActive: cc.isActive !== undefined ? cc.isActive : true
          }));
        // Reapply filters to preserve filtered state
        this.applySearch();
        this.loading = false;
      }
    });
  }

  onSearch() {
    this.applySearch();
  }

  applySearch() {
    let filtered = [...this.costCenters];

    // Apply search text filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(cc =>
        cc.name?.toLowerCase().includes(searchLower) ||
        cc.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedStatusFilter !== null) {
      if (this.selectedStatusFilter === 'active') {
        filtered = filtered.filter(cc => cc.isActive === true);
      } else if (this.selectedStatusFilter === 'inactive') {
        filtered = filtered.filter(cc => cc.isActive === false);
      }
    }

    // Apply Service Group filter
    if (this.selectedServiceGroupFilter) {
      filtered = filtered.filter(cc => cc.serviceGroupId === this.selectedServiceGroupFilter);
    }

    this.filteredCostCenters = filtered;
    this.totalRecords = this.filteredCostCenters.length;
    this.first = 0;
    this.pageHeaderService.setExportDisabled(this.filteredCostCenters.length === 0);
  }

  clearSearch() {
    this.searchText = '';
    this.applySearch();
  }

  onStatusFilterChange() {
    this.applySearch();
  }

  onServiceGroupFilterChange() {
    this.applySearch();
  }

  clearFilters() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = 'active';
    this.selectedServiceGroupFilter = null;
    this.applySearch();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim() ||
      (this.selectedStatusFilter !== null && this.selectedStatusFilter !== 'active') ||
      this.selectedServiceGroupFilter);
  }

  toggleActiveStatus(costCenter: CostCenter) {
    const activating = !costCenter.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${costCenter.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
        const updatedCostCenter = { ...costCenter, isActive: !costCenter.isActive };
        this.costCenterService.updateCostCenter(costCenter.id, updatedCostCenter).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `Cost Center ${updatedCostCenter.isActive ? 'activated' : 'deactivated'} successfully`
            });
            this.loadCostCenters();
          },
          error: (error) => {
            console.error('Error updating cost center status:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update cost center status'
            });
          }
        });
      }
    });
  }

  showAddDialog() {
    this.selectedCostCenter = this.getEmptyCostCenter();
    this.fieldTouched = {};
    this.dialogMode = 'add';
    this.displayDialog = true;
  }

  showEditDialog(costCenter: CostCenter) {
    this.selectedCostCenter = { ...costCenter };
    this.fieldTouched = {};
    this.dialogMode = 'edit';
    this.displayDialog = true;
  }

  hideDialog() {
    this.displayDialog = false;
    this.fieldTouched = {};
    this.selectedCostCenter = this.getEmptyCostCenter();
  }

  resetCostCenterForm() {
    this.selectedCostCenter = this.getEmptyCostCenter();
    this.fieldTouched = {};
  }

  exportToExcel(): void {
    if (!this.filteredCostCenters?.length) {
      this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'No cost centers to export' });
      return;
    }
    try {
      const exportData = this.filteredCostCenters.map(c => ({
        'Name': c.name || '',
        'Description': (c.description || '').slice(0, 200),
        'Service Group': c.serviceGroupName || '',
        'Status': c.isActive ? 'Active' : 'Inactive'
      }));
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      worksheet['!cols'] = [{ wch: 22 }, { wch: 36 }, { wch: 18 }, { wch: 10 }];
      const workbook: XLSX.WorkBook = { Sheets: { 'Cost Centers': worksheet }, SheetNames: ['Cost Centers'] };
      const excelBuffer: unknown = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `CostCenters_${this.formatDateForFile(new Date())}.xlsx`);
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Cost centers exported to Excel successfully' });
    } catch (error) {
      console.error('Error exporting cost centers:', error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to export cost centers to Excel' });
    }
  }

  private formatDateForFile(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  saveCostCenter() {
    this.fieldTouched['name'] = true;
    if (!this.selectedCostCenter.name.trim()) {
      return;
    }

    if (this.dialogMode === 'add') {
      this.costCenterService.addCostCenter(this.selectedCostCenter).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Cost Center added successfully'
          });
          this.loadCostCenters();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error adding cost center:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add cost center'
          });
        }
      });
    } else {
      this.costCenterService.updateCostCenter(this.selectedCostCenter.id, this.selectedCostCenter).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Cost Center updated successfully'
          });
          this.loadCostCenters();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error updating cost center:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update cost center'
          });
        }
      });
    }
  }

  private getEmptyCostCenter(): CostCenter {
    return {
      id: '',
      name: '',
      description: '',
      isActive: true,
      serviceGroupId: null,
      serviceGroupName: null
    };
  }

  onServiceGroupChange(): void {
    if (this.selectedCostCenter.serviceGroupId) {
      const serviceGroup = this.serviceGroups.find(sg => sg.id === this.selectedCostCenter.serviceGroupId);
      this.selectedCostCenter.serviceGroupName = serviceGroup?.name || null;
    } else {
      this.selectedCostCenter.serviceGroupName = null;
    }
  }
}

